import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../utils/api';

const normalize = (value) => (value || '').replace(/[\s-]/g, '').toUpperCase();

export function fillEmptyAircraftDetails(details, aircraft) {
  const fields = {
    aircraft_type: aircraft.type_description || aircraft.model,
    aircraft_type_code: aircraft.type_code,
    aircraft_manufacturer: aircraft.manufacturer,
    aircraft_model: aircraft.model,
  };
  const next = { ...details };
  Object.entries(fields).forEach(([key, value]) => {
    if (!String(next[key] || '').trim() && value) next[key] = value;
  });
  return next;
}

export function useAircraftLookup(setFormData, context) {
  const [aircraftLookupLoading, setLoading] = useState(false);
  const [aircraftLookupError, setError] = useState('');
  const [aircraftLookupSuccess, setSuccess] = useState(false);
  const sequence = useRef(0);
  const timer = useRef(null);
  const controller = useRef(null);

  useEffect(() => {
    sequence.current += 1;
    setLoading(false);
    setError('');
    setSuccess(false);
    return () => {
      sequence.current += 1;
      clearTimeout(timer.current);
      controller.current?.abort();
    };
  }, [context]);

  const handleTailNumberChange = useCallback((value) => {
    const requestId = ++sequence.current;
    clearTimeout(timer.current);
    controller.current?.abort();
    setFormData((prev) => ({ ...prev, component_data: { ...prev.component_data, tail_number: value } }));
    setError('');
    setSuccess(false);
    setLoading(false);
    const registration = normalize(value);
    if (!/^[A-Z0-9]{2,10}$/.test(registration)) return;

    timer.current = setTimeout(async () => {
      controller.current = new AbortController();
      setLoading(true);
      try {
        const response = await axios.post(`${getApiBase()}/aircraft/lookup`, { tail_number: registration }, {
          headers: getAuthHeaders(), withCredentials: true, timeout: 15000,
          signal: controller.current.signal,
        });
        if (sequence.current !== requestId) return;
        const aircraft = response.data.data || response.data.aircraft;
        if (!response.data.success || !aircraft) {
          setError('Aircraft details unavailable. You can enter them manually.');
          return;
        }
        setFormData((prev) => {
          if (sequence.current !== requestId || normalize(prev.component_data?.tail_number) !== registration) return prev;
          return { ...prev, component_data: fillEmptyAircraftDetails(prev.component_data || {}, aircraft) };
        });
        setSuccess(true);
      } catch (error) {
        if (sequence.current !== requestId || axios.isCancel(error)) return;
        setError(error.response?.status === 401
          ? 'Please sign in again. You can enter aircraft details manually.'
          : 'Aircraft lookup unavailable. You can enter details manually.');
      } finally {
        if (sequence.current === requestId) setLoading(false);
      }
    }, 1000);
  }, [setFormData]);

  return { aircraftLookupLoading, aircraftLookupError, aircraftLookupSuccess, handleTailNumberChange };
}
